import React, { memo, useMemo } from 'react';
import type { DailyShiftSlotGrouped } from '../../../../domain/dailyShiftSlot';
import CalendarHeader from './CalendarHeader';
import CalendarDayCell from './CalendarDayCell';
import { CalendarContainer, CalendarGrid, WeekdayHeader } from '../MonthlyShiftSlotSetup.styles';

const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土'];

interface ShiftSlotCalendarProps {
  year: number;
  month: number;
  days: DailyShiftSlotGrouped[];
  onPreviousMonth?: () => void;
  onNextMonth?: () => void;
  onDayClick: (day: DailyShiftSlotGrouped) => void;
  disabled?: boolean;
  showHeader?: boolean;
}

/**
 * シフト枠カレンダーコンポーネント
 *
 * - カレンダーグリッド（7列）
 * - 曜日ヘッダー
 * - CalendarHeader統合
 * - CalendarDayCell統合
 */
const ShiftSlotCalendar: React.FC<ShiftSlotCalendarProps> = memo(({
  year,
  month,
  days,
  onPreviousMonth,
  onNextMonth,
  onDayClick,
  disabled = false,
  showHeader = true,
}) => {
  // カレンダーグリッド生成
  const calendarGrid = useMemo(() => {
    const grid: (DailyShiftSlotGrouped | null)[] = [];

    // 月初の曜日を取得
    const firstDayOfMonth = new Date(year, month - 1, 1);
    const startDayOfWeek = firstDayOfMonth.getDay();

    // 月初までの空セル
    for (let i = 0; i < startDayOfWeek; i++) {
      grid.push(null);
    }

    // 日付データをマッピング
    const daysMap = new Map<string, DailyShiftSlotGrouped>();
    days.forEach(day => {
      daysMap.set(day.date, day);
    });

    // 月の日数を取得
    const lastDayOfMonth = new Date(year, month, 0);
    const daysInMonth = lastDayOfMonth.getDate();

    // 各日のセルを追加
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      grid.push(daysMap.get(dateStr) || null);
    }

    // 月末以降の空セル（7の倍数になるまで）
    while (grid.length % 7 !== 0) {
      grid.push(null);
    }

    return grid;
  }, [year, month, days]);

  return (
    <CalendarContainer>
      {showHeader && onPreviousMonth && onNextMonth && (
        <CalendarHeader
          year={year}
          month={month}
          onPreviousMonth={onPreviousMonth}
          onNextMonth={onNextMonth}
          disabled={disabled}
        />
      )}

      <CalendarGrid>
        {/* 曜日ヘッダー */}
        {WEEKDAY_LABELS.map((label, index) => (
          <WeekdayHeader
            key={label}
            $isSunday={index === 0}
            $isSaturday={index === 6}
          >
            {label}
          </WeekdayHeader>
        ))}

        {/* 日付セル */}
        {calendarGrid.map((day, index) => (
          <CalendarDayCell
            key={index}
            day={day}
            onClick={day ? onDayClick : undefined}
          />
        ))}
      </CalendarGrid>
    </CalendarContainer>
  );
});

ShiftSlotCalendar.displayName = 'ShiftSlotCalendar';

export default ShiftSlotCalendar;
